import type { CommandBatch } from '../../editor/commandHistory.js';
import type { EditorNavigationController } from '../editorNavigation.js';
import type { EditorSidebarController } from '../editorSidebar.js';
import type { LibraryKind } from '../editorTargetInfo.js';
import type { SceneNodeId } from '../../scene/sceneState.js';
import { createPropRow } from './widgets.js';
import {
  type SceneTreeSelectionController,
} from './selection.js';
import {
  type SceneTreePropertyActionDeps,
} from './propertyActions.js';
import type { AppState } from '../../state/appState.js';
import type { EditorCommands } from '../../editor/commands.js';
import { findLayerGraphLibraryItem } from '../../scene/layerState.js';
import type { GeometrySlot, MaterialSlot } from '../../scene/layerState.js';
import type { GraphNode } from '../../graph/nodeDefs.js';
import { getExposedValueNodes, renderExposedParams } from '../params.js';
import type { ParamValue } from '../../graph/nodeDefs.js';
import { refreshIntrinsicSizeDisplay } from './intrinsicSizeDisplay.js';

export interface SlotInspectorDeps extends SceneTreePropertyActionDeps {
  appState: AppState;
  commandBatch: CommandBatch;
  commands: SceneTreePropertyActionDeps['commands'] & Pick<EditorCommands, 'setParamValue' | 'setNodeParamOverride'>;
  selection: SceneTreeSelectionController;
  editorSidebar: Pick<
    EditorSidebarController,
    | 'attachLibraryToComponent'
    | 'createLibraryItem'
    | 'detachLibraryToInline'
    | 'libraryName'
  >;
  editorNavigation: Pick<EditorNavigationController, 'openComponentSlotEditor'>;
}

function resolveSlotParamGraph(
  appState: AppState,
  kind: LibraryKind,
  slot: GeometrySlot | MaterialSlot,
  layerId: string,
): { nodes: GraphNode[]; scopeId: string } | null {
  if (slot.type === 'inline') {
    return { nodes: slot.nodes, scopeId: layerId };
  }
  const item = findLayerGraphLibraryItem(appState, kind, slot.id);
  return item ? { nodes: item.nodes, scopeId: layerId } : null;
}

function renderSlotParamInspector(
  deps: SlotInspectorDeps,
  group: HTMLElement,
  kind: LibraryKind,
  slot: GeometrySlot | MaterialSlot,
  layerId: string,
  ownerNodeId: SceneNodeId,
): void {
  const graph = resolveSlotParamGraph(deps.appState, kind, slot, layerId);
  if (!graph || getExposedValueNodes(graph.nodes).length === 0) return;

  const params = document.createElement('div');
  params.className = 'scene-slot-params';

  const list = document.createElement('div');
  list.className = 'scene-slot-params-list';
  params.appendChild(list);

  // The panel edits the node that is selected, which means its override and not
  // the layer's own value: the override is what wins when the frame is drawn, so
  // editing the layer value under an overridden node lands and changes nothing.
  const node = deps.appState.scene.nodes[ownerNodeId];
  const overridable = node?.kind === 'layer' || node?.kind === 'instance';

  renderExposedParams({
    appState: deps.appState,
    commandBatch: deps.commandBatch,
    setParamValue: overridable
      ? (key, value) => deps.commands.setNodeParamOverride(ownerNodeId, key, value)
      : deps.commands.setParamValue,
    readParam: overridable
      ? (key) => {
        const current = deps.appState.scene.nodes[ownerNodeId];
        if (current?.kind !== 'layer' && current?.kind !== 'instance') return undefined;
        return (current.paramOverrides as Record<string, ParamValue>)[key];
      }
      : undefined,
    scheduleRender: deps.scheduleRender,
    refreshIntrinsicSizeDisplay: () => refreshIntrinsicSizeDisplay(deps.appState),
  }, {
    container: list,
    nodes: graph.nodes,
    scopeId: graph.scopeId,
    slot: kind,
  });

  group.appendChild(params);
}

export function setInlineLayerGeometrySizeMode(
  deps: Pick<SlotInspectorDeps, 'appState' | 'scheduleRender'>,
  layerId: string,
  sizeMode: { kind: 'axes'; width: 'fill' | 'content'; height: 'fill' | 'content' } | undefined,
): boolean {
  const layerDef = deps.appState.layerLibrary[layerId];
  if (!layerDef || layerDef.geometry.type !== 'inline') return false;
  layerDef.geometry = { ...layerDef.geometry, sizeMode };
  deps.scheduleRender();
  return true;
}

function visibleLibraryItems(appState: AppState, kind: LibraryKind): Array<[string, { name: string }]> {
  const groups = [
    kind === 'geometry' ? appState.geometryLibrary.builtIn : appState.materialLibrary.builtIn,
    kind === 'geometry' ? appState.geometryLibrary.user : appState.materialLibrary.user,
  ];

  return groups.flatMap(group => Object.entries(group)
    .map(([id, item]) => [id, item] as [string, { name: string }]));
}

export function renderLayerSlotInspector(
  deps: SlotInspectorDeps,
  panel: HTMLElement,
  componentId: string | null,
  ownerNodeId: SceneNodeId,
  kind: LibraryKind,
  layerId: string,
): void {
  const { appState, editorNavigation, editorSidebar } = deps;
  const layerDef = appState.layerLibrary[layerId];
  if (!layerDef) return;
  const slot = kind === 'geometry' ? layerDef.geometry : layerDef.material;
  const group = document.createElement('div');
  group.className = 'scene-slot-prop-group';
  const row = createPropRow('source');
  row.classList.add('scene-prop-row--slot');
  const sourceSelect = document.createElement('select');
  sourceSelect.className = 'scene-prop-input';

  if (slot.type === 'inline') {
    const inlineOption = document.createElement('option');
    inlineOption.value = '__inline__';
    inlineOption.textContent = 'Inline';
    inlineOption.selected = true;
    sourceSelect.appendChild(inlineOption);
  } else {
    const currentOption = document.createElement('option');
    currentOption.value = slot.id;
    currentOption.textContent = editorSidebar.libraryName(kind, slot.id);
    currentOption.selected = true;
    sourceSelect.appendChild(currentOption);
  }

  if (slot.type !== 'inline') {
    const inlineOption = document.createElement('option');
    inlineOption.value = '__inline__';
    inlineOption.textContent = 'Inline';
    sourceSelect.appendChild(inlineOption);
  }

  for (const [id, item] of visibleLibraryItems(appState, kind)) {
    if (slot.type === 'library' && id === slot.id) continue;
    const opt = document.createElement('option');
    opt.value = id;
    opt.textContent = item.name;
    sourceSelect.appendChild(opt);
  }

  sourceSelect.addEventListener('change', () => {
    deps.selection.setSelectedVirtualTarget(null);
    deps.selection.setSelectedSceneNodeId(ownerNodeId);
    if (sourceSelect.value === '__inline__') {
      if (slot.type === 'library') editorSidebar.detachLibraryToInline(kind, componentId, layerId);
      return;
    }
    editorSidebar.attachLibraryToComponent(kind, componentId, sourceSelect.value, layerId);
  });
  row.appendChild(sourceSelect);

  const editBtn = document.createElement('button');
  editBtn.className = 'scene-tree-chip scene-slot-edit-button';
  editBtn.textContent = 'Edit';
  editBtn.title = `Edit ${kind} graph`;
  editBtn.addEventListener('click', () => {
    deps.selection.setSelectedVirtualTarget(null);
    deps.selection.setSelectedSceneNodeId(ownerNodeId);
    editorNavigation.openComponentSlotEditor(componentId, kind, true, layerId);
  });
  row.appendChild(editBtn);
  group.appendChild(row);
  renderSlotParamInspector(deps, group, kind, slot, layerId, ownerNodeId);
  panel.appendChild(group);
}

export function setLayerIntrinsicSizeMode(
  deps: Pick<SlotInspectorDeps, 'appState' | 'scheduleRender'>,
  layerId: string,
): boolean {
  return setInlineLayerGeometrySizeMode(
    deps, layerId, { kind: 'axes', width: 'content', height: 'content' },
  );
}
